import React from 'react';
import { Icon } from '@iconify/react';
import toast from 'react-hot-toast';
import { Link } from '../components/common/NextAdapters';
import { RequestTitleEditModal } from '../components/request-title-edit-modal';
import { RequestEpisodeEditModal } from '../components/request-episode-edit-modal';
import { useMyMovies, useCancelMovieEdit, useCancelEpisodeEdit } from '../hooks/useMovie';
import { useAuthStore } from '../store';
import { getApiErrorMessage } from '../api/errors';
import { usePageTitle } from '../hooks/usePageTitle';

const STATUS_STYLES = {
  Approved: 'bg-emerald-500/10 text-emerald-400 ring-emerald-500/25',
  Pending: 'bg-primary/10 text-primary ring-primary/25',
  Processing: 'bg-cyan-500/10 text-cyan-400 ring-cyan-500/25',
  Rejected: 'bg-destructive/10 text-destructive ring-destructive/25',
};

function StatusBadge({ status }) {
  const style = STATUS_STYLES[status] || 'bg-white/5 text-muted-foreground ring-white/10';
  const label = status === 'Approved' ? 'Live' : status;
  return (
    <span className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ${style}`}>
      {label}
    </span>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="rounded-xl bg-white/[0.03] px-4 py-3.5 ring-1 ring-foreground/[0.06]">
      <div className={`text-2xl font-bold tabular-nums ${accent}`}>{value}</div>
      <div className="mt-0.5 text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function ActionTile({ to, icon, iconClass, label, disabled }) {
  const inner = (
    <>
      <span className={`flex size-8 shrink-0 items-center justify-center rounded-md bg-white/5 ${iconClass}`}>
        <Icon icon={icon} className="size-4" />
      </span>
      <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground/90">{label}</span>
      {!disabled && (
        <Icon
          icon="ph:arrow-right-bold"
          className="size-3.5 shrink-0 text-muted-foreground/30 transition-transform group-hover:translate-x-0.5 group-hover:text-muted-foreground"
        />
      )}
    </>
  );

  if (disabled) {
    return (
      <div
        className="flex cursor-not-allowed items-center gap-3 rounded-lg px-3.5 py-3 opacity-[0.45] ring-1 ring-foreground/[0.06]"
        title="Awaiting admin approval"
      >
        {inner}
      </div>
    );
  }

  return (
    <Link
      href={to}
      className="group flex items-center gap-3 rounded-lg px-3.5 py-3 ring-1 ring-foreground/[0.06] transition-colors hover:bg-white/[0.04] hover:ring-foreground/15"
    >
      {inner}
    </Link>
  );
}

function EmptyState({ icon, children }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-xl py-16 text-center ring-1 ring-foreground/[0.06]">
      <Icon icon={icon} className="size-6 text-muted-foreground/40" />
      <p className="max-w-sm text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

function EpisodeRow({ ep, canEdit }) {
  const [editing, setEditing] = React.useState(false);
  const cancelEdit = useCancelEpisodeEdit();
  const pendingEdit = ep.PendingEdit;
  const rejectedEdit = ep.LastReviewedEdit?.Status === 'Rejected' ? ep.LastReviewedEdit : null;

  const handleWithdraw = async () => {
    try {
      await cancelEdit.mutateAsync(pendingEdit.Id);
      toast.success('Edit withdrawn.');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not withdraw that edit.'));
    }
  };

  return (
    <div className="text-sm text-muted-foreground">
      {editing && <RequestEpisodeEditModal episode={ep} onClose={() => setEditing(false)} />}

      <div className="flex items-center gap-2.5">
        <Icon icon="ph:play-circle-bold" className="shrink-0 text-muted-foreground/40" />
        <span className="min-w-0 flex-1 truncate">Ep {ep.EpisodeNumber}: {ep.Title}</span>
        <StatusBadge status={ep.ApprovalStatus} />
        {pendingEdit ? (
          <button
            type="button"
            onClick={handleWithdraw}
            disabled={cancelEdit.isPending}
            className="shrink-0 text-xs font-medium text-muted-foreground transition-colors hover:text-destructive disabled:opacity-50"
          >
            Withdraw edit
          </button>
        ) : canEdit ? (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="shrink-0 text-xs font-medium text-primary transition-colors hover:text-primary/80"
          >
            Request edit
          </button>
        ) : null}
      </div>

      {pendingEdit && (
        <p className="ml-6 mt-1 flex items-center gap-1.5 text-xs text-primary">
          <Icon icon="ph:clock-bold" className="size-3.5 shrink-0" />
          Edit awaiting admin review ({pendingEdit.ChangedFields?.join(', ')})
        </p>
      )}
      {rejectedEdit && (
        <p className="ml-6 mt-1 flex items-start gap-1.5 text-xs text-destructive">
          <Icon icon="ph:x-circle-bold" className="mt-0.5 size-3.5 shrink-0" />
          <span>Your last edit was rejected{rejectedEdit.ReviewNote ? `: ${rejectedEdit.ReviewNote}` : '.'}</span>
        </p>
      )}
    </div>
  );
}

function TitleCard({ movie, canEdit }) {
  const [showEpisodes, setShowEpisodes] = React.useState(false);
  const [editing, setEditing] = React.useState(false);
  const cancelEdit = useCancelMovieEdit();
  const hasEpisodes = movie.Episodes?.length > 0;
  const pendingEdit = movie.PendingEdit;
  const rejectedEdit = movie.LastReviewedEdit?.Status === 'Rejected' ? movie.LastReviewedEdit : null;
  const canRequestEdit = canEdit && movie.ApprovalStatus !== 'Rejected';

  const handleWithdrawEdit = async () => {
    try {
      await cancelEdit.mutateAsync(pendingEdit.Id);
      toast.success('Edit withdrawn.');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not withdraw that edit.'));
    }
  };

  return (
    <div className="rounded-lg bg-white/[0.03] p-3.5 ring-1 ring-foreground/[0.06]">
      {editing && <RequestTitleEditModal movie={movie} onClose={() => setEditing(false)} />}

      <div className="flex items-center gap-3.5">
        {movie.ThumbnailUrl ? (
          <img
            src={movie.ThumbnailUrl}
            alt={movie.Title}
            className="h-14 w-24 shrink-0 rounded-md object-cover ring-1 ring-white/10"
          />
        ) : (
          <div className="flex h-14 w-24 shrink-0 items-center justify-center rounded-md bg-white/5 text-muted-foreground/40 ring-1 ring-white/10">
            <Icon icon="ph:film-slate-bold" className="size-5" />
          </div>
        )}

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-foreground">{movie.Title}</p>
          <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs text-muted-foreground">
            <span>{movie.ContentType}</span>
            {movie.CategoryName && <span>{movie.CategoryName}</span>}
            <span>Uploaded {movie.DateCreated?.split('T')[0]}</span>
          </div>
          {movie.BunnyPushError && (
            <p className="mt-1.5 flex items-center gap-1.5 text-xs text-destructive">
              <Icon icon="ph:warning-bold" className="size-3.5 shrink-0" />
              {movie.BunnyPushError}
            </p>
          )}
          {pendingEdit && (
            <p className="mt-1.5 flex items-center gap-1.5 text-xs text-primary">
              <Icon icon="ph:clock-bold" className="size-3.5 shrink-0" />
              Edit awaiting admin review ({pendingEdit.ChangedFields?.join(', ')})
            </p>
          )}
          {rejectedEdit && (
            <p className="mt-1.5 flex items-start gap-1.5 text-xs text-destructive">
              <Icon icon="ph:x-circle-bold" className="mt-0.5 size-3.5 shrink-0" />
              <span>
                Your last edit was rejected{rejectedEdit.ReviewNote ? `: ${rejectedEdit.ReviewNote}` : '.'}
              </span>
            </p>
          )}
        </div>

        <div className="flex shrink-0 flex-col items-end gap-2">
          <StatusBadge status={movie.ApprovalStatus} />
          {pendingEdit ? (
            <button
              type="button"
              onClick={handleWithdrawEdit}
              disabled={cancelEdit.isPending}
              className="flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-destructive disabled:opacity-50"
            >
              <Icon icon="ph:arrow-u-up-left-bold" />
              Withdraw edit
            </button>
          ) : canRequestEdit ? (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="flex items-center gap-1 text-xs font-medium text-primary transition-colors hover:text-primary/80"
            >
              <Icon icon="ph:pencil-simple-bold" />
              Request edit
            </button>
          ) : null}
          {hasEpisodes && (
            <button
              type="button"
              onClick={() => setShowEpisodes((s) => !s)}
              className="flex items-center gap-1 text-xs font-medium text-primary transition-colors hover:text-primary/80"
            >
              <Icon icon={showEpisodes ? 'ph:caret-up-bold' : 'ph:caret-down-bold'} />
              {showEpisodes ? 'Hide' : 'Show'} episodes
            </button>
          )}
        </div>
      </div>

      {showEpisodes && hasEpisodes && (
        <div className="mt-3 flex flex-col gap-2 border-t border-white/5 pt-3">
          {Object.entries(
            movie.Episodes.reduce((groups, ep) => {
              const key = ep.SeasonNumber ?? '__none__';
              (groups[key] = groups[key] || []).push(ep);
              return groups;
            }, {})
          ).map(([seasonKey, eps]) => (
            <div key={seasonKey}>
              {seasonKey !== '__none__' && (
                <p className="mb-1 text-xs font-semibold text-muted-foreground">Season {seasonKey}</p>
              )}
              <div className="flex flex-col gap-1.5">
                {eps.map((ep) => (
                  <EpisodeRow key={ep.Id} ep={ep} canEdit={canRequestEdit} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CreatorDashboardPage() {
  usePageTitle('Creator Dashboard · Kast');
  const { data: movies = [], isLoading, error } = useMyMovies();
  const { user } = useAuthStore();
  // Admins hitting this page (via RequireCreator) always have effective upload access even
  // though CanUploadContent is only meaningful for the ContentCreator role.
  const canUpload = user?.Role === 'Admin' || !!user?.CanUploadContent;

  const counts = {
    total: movies.length,
    live: movies.filter((m) => m.ApprovalStatus === 'Approved').length,
    pending: movies.filter((m) => m.ApprovalStatus === 'Pending').length,
    processing: movies.filter((m) => m.ApprovalStatus === 'Processing').length,
    rejected: movies.filter((m) => m.ApprovalStatus === 'Rejected').length,
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 sm:py-8 lg:px-10">
      <p className="mb-6 text-sm text-muted-foreground">Your uploads and their approval status.</p>

      {!canUpload && (
          <div className="mb-6 flex items-center gap-3 rounded-xl border border-primary/25 bg-primary/[0.07] px-4 py-3 text-sm text-primary">
            <Icon icon="ph:clock-bold" className="size-4 shrink-0" />
            <span>
              Your producer account is awaiting admin approval. You'll be able to upload movies and
              episodes, and be notified by email, once an admin grants you upload access.
            </span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-5">
          <StatCard label="Total Titles" value={counts.total} accent="text-foreground" />
          <StatCard label="Live" value={counts.live} accent="text-emerald-400" />
          <StatCard label="Pending Review" value={counts.pending} accent="text-primary" />
          <StatCard label="Processing" value={counts.processing} accent="text-cyan-400" />
          <StatCard label="Rejected" value={counts.rejected} accent="text-destructive" />
        </div>

        <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-3">
          <ActionTile
            to="/upload/movie"
            icon="ph:upload-simple-bold"
            iconClass="text-destructive"
            label="Upload Movie / Series"
            disabled={!canUpload}
          />
          <ActionTile
            to="/upload/episode"
            icon="ph:upload-simple-bold"
            iconClass="text-primary"
            label="Upload Episode"
            disabled={!canUpload}
          />
          <ActionTile
            to="/creator/payouts"
            icon="ph:currency-dollar-bold"
            iconClass="text-emerald-400"
            label="My Payouts"
          />
        </div>

        <h2 className="mb-2.5 mt-9 text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">
          Your Titles
        </h2>

        {isLoading ? (
          <EmptyState icon="ph:spinner-gap-bold">Loading…</EmptyState>
        ) : error ? (
          <EmptyState icon="ph:warning-bold">{getApiErrorMessage(error, 'Failed to load your uploads.')}</EmptyState>
        ) : movies.length === 0 ? (
          <EmptyState icon="ph:film-slate-bold">
            You haven't uploaded anything yet. Use "Upload Movie / Series" above to get started.
          </EmptyState>
        ) : (
          <div className="flex flex-col gap-2.5">
            {movies.map((m) => (
              <TitleCard key={m.Id} movie={m} canEdit={canUpload} />
            ))}
          </div>
        )}
    </div>
  );
}
